import { ChangeDetectorRef, Component, input, ChangeDetectionStrategy } from '@angular/core';
import { take } from 'rxjs/operators';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { AuctionBidAdmin } from '../../models/auctionBidAdmin';
import { PropertyBidHistory } from '../../models/propertyBidHistory';
import { UserManagementApiService } from '../../services/UserManagementApiService';
import { UserBasicDataResponse } from '../../responses/userBasicDataResponse';
import { AuctionService } from '../../services/AuctionService';
import { UserContactDialogComponent } from '../dialog/user-contact-dialog.component';

@Component({ standalone: false,
  selector: 'app-bidding-history-admin',
  templateUrl: 'bidding-history-admin.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['bidding-history-admin.component.scss']
})
export class BiddingHistoryAdminComponent {
  readonly propertyId = input<number|undefined>(undefined);
  readonly auctionIsLive = input<boolean>(false);
  readonly updateUponInit = input<boolean>(false);
  readonly auctionType = input<number|undefined>(undefined);
  public title: string= "Bidding History";
  public confirmButtonText = "OK";
  public busy:boolean = false;

  public dataSourceArray:MatTableDataSource<AuctionBidAdmin>[] = [];
  public propertyBidHistory?:PropertyBidHistory;

  constructor(private auctionService:AuctionService, private umService: UserManagementApiService, private dialog: MatDialog, private cdRef:ChangeDetectorRef) {
  }

  ngOnInit(){
    if(this.updateUponInit()) this.getPropertyBidHistory();
  }

  get displayedColumns(){
    if(this.auctionType()==1) return ['userName','name', 'role', 'amount', 'time', 'autobid', 'equalized', 'actions'];
    else return ['userName','name', 'role', 'amount', 'time', 'bidAccepted', 'askAmount', 'askAmountExclusivitySeconds', 'askAccepted', 'actions'];
  }

  public update(){
    this.getPropertyBidHistory();
  }

  private getPropertyBidHistory(){
    this.dataSourceArray=[];
    if(this.propertyId()){
      this.busy=true;
      this.auctionService.getPropertyBidHistory(this.propertyId()!).pipe(take(1)).subscribe(pbh=>{
        this.propertyBidHistory=pbh;
        this.propertyBidHistory.auctionBidHistories.forEach(abh => {
          //this needs to be changed: if an auctionBid has a proxBidHistory, I need to replicate the bid per proxyChange except the first one.
          //I also want to display SET,INC,DEC,EQU in the table 
          if(abh.auctionBids) this.dataSourceArray.push(new MatTableDataSource(this.processAuctionBids(abh.auctionBids)));
        });
        this.busy=false;
        this.cdRef.detectChanges();
      });
    }
  }

  private processAuctionBids(auctionBids:AuctionBidAdmin[]):AuctionBidAdmin[]{
    let auctionBidsWithProxyChanges:AuctionBidAdmin[]=auctionBids;
    //find distinct auction bids who have a proxy bid history with more than 1 entry
    let distinctAuctionBids:AuctionBidAdmin[]=Array.from(
      auctionBids.reduce((map, item) => {
          if (!map.has(item.id)) {
              map.set(item.id, item);
          }
          return map;
      }, new Map<number, AuctionBidAdmin>()).values()
    ).filter(ab => ab.proxyBidHistory && ab.proxyBidHistory.length>1);
    //for each of these distinct auction bids with proxy bid history, we need to clone the bid before the proxy change for each proxy change except the first one
    distinctAuctionBids.forEach(ab=>{
      if(ab.proxyBidHistory && ab.proxyBidHistory?.length>1){
        //duplicate bid for display purposes but ignore last proxyBidHistory item as it is the initial one (SET)) 
        for(let i=0;i<ab.proxyBidHistory.length-1;i++){
          //look if we have a real bid at the same time, if not create an artifical bid to indicate the proxy change and use the bidamount from the bid before the proxy change
          const searchIndex = auctionBidsWithProxyChanges.findIndex(a => a.bidDateTime==ab.proxyBidHistory![i].timeStamp);
          if(searchIndex==-1){
            //only clone if we don't already have a bid with this timestamp
            var lastBidBeforeProxyChangeIndex =  auctionBidsWithProxyChanges.findIndex(a => a.bidDateTime.getTime()<=ab.proxyBidHistory![i].timeStamp.getTime());
            if(lastBidBeforeProxyChangeIndex==-1) lastBidBeforeProxyChangeIndex = auctionBidsWithProxyChanges.length; //should not happen as we should always have at least the initial bid
            let clone = Object.assign(new AuctionBidAdmin(),auctionBidsWithProxyChanges[lastBidBeforeProxyChangeIndex]);
            clone.bidAmount = ab.proxyBidHistory[i].amount;
            clone.bidDateTime = ab.proxyBidHistory[i].timeStamp;
            clone.historicProxyAction = ab.proxyBidHistory[i].action;
            auctionBidsWithProxyChanges.splice(lastBidBeforeProxyChangeIndex, 0, clone);
          }
        }
      }
    });
    
    return auctionBidsWithProxyChanges;
  }

  public onBidderInformation(auctionBid:AuctionBidAdmin){
    this.umService.getUserBasicData(auctionBid.userId).pipe(take(1)).subscribe(r=>{
      this.userContactDialog(r, auctionBid);
    });
  }
  
  private userContactDialog(r:UserBasicDataResponse, auctionBid:AuctionBidAdmin){
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    let auctionBidHistory = auctionBid.scheduleSeqNo? this.propertyBidHistory?.auctionBidHistories.find(abh => abh.scheduleSeqNo==auctionBid.scheduleSeqNo):this.propertyBidHistory?.auctionBidHistories[0];
    let userBids = auctionBidHistory?.auctionBids?.filter(ab => ab.userId==auctionBid.userId);
    dialogConfig.data={user:r, userBids: userBids, auctionIsLive:this.auctionIsLive()};

    this.dialog.open(UserContactDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(value=>{
      if(value){
        //refresh bidding history
        this.getPropertyBidHistory();
      }
      else{
        //do nothing
      }
    });
  }

}